import { CalendarClock, CheckCircle2, Clipboard, Database, DoorOpen, Download, ExternalLink, KeyRound, Link2, ListChecks, Route, Send, ShieldCheck, TriangleAlert, Truck, Webhook, Workflow, Wrench } from 'lucide-react'
import { useState } from 'react'
import { Button, Field, LoadingBlock } from '../../components/shared'
import { exportJson } from '../../utils/export'

export default function IntegrationSettings({ settings, onSettings, records, drivers, vehicles }) {
  const [url, setUrl] = useState(settings.webhookUrl)
  const [testing, setTesting] = useState(false)
  const [result, setResult] = useState(null)
  const [integrationSaved, setIntegrationSaved] = useState(false)
  const defaults = {
    attendance: { method: '未設定', enabled: false },
    dispatch: { method: '未設定', enabled: false },
    fleet: { method: '未設定', enabled: false },
    gatebell: { method: '未設定', enabled: false },
    maintenance: { method: '未設定', enabled: false },
  }
  const [integrations, setIntegrations] = useState(() => Object.fromEntries(Object.entries(defaults).map(([key, value]) => [key, { ...value, ...(settings.integrations?.[key] || {}) }])))
  const schema = `POST /api/v1/safety-checks\n{\n  "employee_id": "DRV-001",\n  "assignment_id": "DSP-20260718-001",\n  "vehicle_id": "v01",\n  "vehicle_number": "品川 100 あ 12-34",\n  "checked_at": "2026-07-18T07:42:00+09:00",\n  "phase": "before_work",\n  "value_mg_l": 0.00,\n  "alcohol_detected": false,\n  "pre_drive_check": "completed",\n  "vehicle_issue": false,\n  "status": "completed"\n}`
  const targets = [
    { id: 'attendance', icon: CalendarClock, name: '勤怠システム', direction: 'ALCO LOG → 勤怠', detail: '社員番号、確認日時、業務前チェック完了状態を連携', fields: 'employee_id / checked_at / status' },
    { id: 'dispatch', icon: Route, name: '配車システム', direction: '配車 ↔ ALCO LOG', detail: '当日の運転者・車両割当を受け取り、実施状態を返却', fields: 'assignment_id / driver_id / vehicle_id' },
    { id: 'fleet', icon: Truck, name: '車両管理システム', direction: '車両管理 → ALCO LOG', detail: '車両番号、車種、所属拠点、稼働状態を同期', fields: 'vehicle_id / number / model / office' },
    { id: 'gatebell', icon: DoorOpen, name: 'GateBell', direction: 'ALCO LOG → GateBell', detail: '安全点呼済みの運転者・車両を現場到着、ゲート受付、入退場記録へ引き継ぐ', fields: 'driver_id / vehicle_id / safety_status / checked_at' },
    { id: 'maintenance', icon: Wrench, name: '車両点検・整備', direction: 'ALCO LOG → 整備対応', detail: 'ブレーキ、異音、警告灯などの異常を整備依頼・代替車両判断へ連携', fields: 'vehicle_id / inspection_item / severity / note' },
  ]

  const save = async () => {
    try {
      await onSettings({ ...settings, webhookUrl: url, webhookEnabled: Boolean(url), integrations })
      setResult({ ok: true, message: '連携設定を保存しました' })
    } catch (error) { setResult({ ok: false, message: error.message || '保存できませんでした' }) }
  }

  const changeIntegration = (id, method) => {
    setIntegrations((current) => ({ ...current, [id]: { method, enabled: method !== '未設定' } }))
    setIntegrationSaved(false)
  }

  const saveIntegrations = async () => {
    try {
      await onSettings({ ...settings, integrations })
      setIntegrationSaved(true)
    } catch (error) {
      setIntegrationSaved(false)
      setResult({ ok: false, message: error.message || '連携方針を保存できませんでした' })
    }
  }

  const test = async () => {
    if (!/^https?:\/\//.test(url)) return setResult({ ok: false, message: 'https:// または http:// から始まるURLを入力してください' })
    setTesting(true); setResult(null)
    try {
      const response = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Alco-Log-Event': 'connection.test' }, body: JSON.stringify({ event: 'connection.test', sent_at: new Date().toISOString(), source: 'ALCO LOG demo' }) })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      setResult({ ok: true, message: 'テスト送信に成功しました' })
      onSettings({ ...settings, webhookUrl: url, webhookEnabled: true, lastSync: new Date().toISOString() })
    } catch (error) {
      setResult({ ok: false, message: `送信できませんでした（${error.message}）。接続先のURLとCORS設定を確認してください。` })
    } finally { setTesting(false) }
  }

  return (
    <>
      <div className="page-heading"><div><h2>連携設定</h2><p>他アプリへのデータ出力と、Webhook/API連携の受け渡し口を設定します。</p></div></div>
      <div className="integration-layout">
        <section className="operation-profile" aria-label="運用前提">
          <div><Truck /><span><small>対象車両</small><strong>白ナンバー／資材配送</strong></span></div>
          <div><ShieldCheck /><span><small>管理区分</small><strong>安全運転管理者制度</strong></span></div>
          <div><Database /><span><small>チェッカー</small><strong>未選定・機種非依存</strong></span></div>
          <p><TriangleAlert /><span>安全運転管理者の選任要否は車両台数の確認後に確定します。未実施時は警告のみで、勤怠・配車操作は止めません。</span></p>
        </section>
        <section className="settings-section compliance-readiness">
          <header><ListChecks /><span><h3>最低限のコンプライアンス構成</h3><p>フルスペック機能ではなく、白ナンバー資材配送の記録と運用に必要な範囲を先に整えます。</p></span></header>
          <div className="compliance-grid">
            <article className="ready"><CheckCircle2 /><span><strong>酒気帯び確認の記録</strong><small>確認者・運転者・車両・日時・方法・有無・指示・備考</small></span></article>
            <article className="ready"><CheckCircle2 /><span><strong>業務前後・1年間保存</strong><small>クラウド接続時に共有保存、月報・CSV・PDFへ出力</small></span></article>
            <article className="ready"><CheckCircle2 /><span><strong>運転前点呼・日常点検</strong><small>体調、疲労、睡眠不足、車両点検と異常報告</small></span></article>
            <article className="warning"><TriangleAlert /><span><strong>検知器の常時有効保持</strong><small>機種、使用期限、作動点検日を本番導入前に登録</small></span></article>
          </div>
          <p><TriangleAlert />現在の公開URLは体験モードです。実運用前にSupabase接続、認証、バックアップ、検知器台帳の設定が必要です。</p>
        </section>
        <section className="settings-section integration-hub">
          <header><Workflow /><span><h3>連携先とデータの流れ</h3><p>GateBellは安全点呼済み情報を受け渡し、その他は製品確定後に方式と項目を合わせます。</p></span></header>
          <div className="integration-target-list">
            {targets.map(({ id, icon: Icon, name, direction, detail, fields }) => (
              <article className="integration-target-row" key={id}>
                <span className="integration-target-icon"><Icon /></span>
                <span className="integration-target-copy"><strong>{name}</strong><small>{direction}</small><p>{detail}</p><code>{fields}</code></span>
                <label><span>連携方式</span><select aria-label={`${name}の連携方式`} value={integrations[id].method} onChange={(event) => changeIntegration(id, event.target.value)}><option>未設定</option><option>REST API</option><option>Webhook</option><option>CSV取込</option><option>CSV出力</option></select></label>
              </article>
            ))}
          </div>
          <div className="settings-actions integration-save"><span className={integrationSaved ? 'saved' : ''}>{integrationSaved ? '保存しました' : '接続先製品の確定後にURL・認証情報を設定します'}</span><Button onClick={saveIntegrations}>連携方針を保存</Button></div>
        </section>
        <section className="settings-section">
          <header><Webhook /><span><h3>Webhook送信</h3><p>チェック登録時に他システムへJSONを送信する想定です。</p></span></header>
          <Field label="送信先URL" hint="本番ではサーバー側から署名付きで送信します">
            <input value={url} onChange={(event) => setUrl(event.target.value)} placeholder="https://example.com/webhooks/alcohol-check" />
          </Field>
          <div className="settings-actions"><Button variant="secondary" onClick={save}>設定を保存</Button><Button icon={Send} onClick={test} disabled={testing}>{testing ? <LoadingBlock label="送信中" /> : 'テスト送信'}</Button></div>
          {result ? <p className={`connection-result ${result.ok ? 'ok' : 'error'}`}>{result.ok ? <CheckCircle2 /> : <Link2 />}{result.message}</p> : null}
        </section>
        <section className="settings-section">
          <header><Database /><span><h3>データエクスポート</h3><p>基幹システムやBIへ渡せるJSON形式で保存します。</p></span></header>
          <div className="export-line"><span><strong>全チェック記録</strong><small>{records.length}件／ドライバー・車両マスタを含む</small></span><Button icon={Download} variant="secondary" onClick={() => exportJson({ exportedAt: new Date().toISOString(), drivers, vehicles, records }, 'alco-log-export.json')}>JSON出力</Button></div>
        </section>
        <section className="settings-section integration-api">
          <header><KeyRound /><span><h3>API連携仕様</h3><p>本番バックエンドで提供するREST APIの想定スキーマです。</p></span></header>
          <div className="api-key-line"><span>接続キー識別子</span><code>{settings.apiKeyLabel}</code></div>
          <pre>{schema}</pre>
          <div className="settings-actions"><Button icon={Clipboard} variant="secondary" onClick={() => navigator.clipboard?.writeText(schema)}>仕様をコピー</Button><a href="../../SOURCE_NOTES.md" target="_blank" rel="noreferrer">根拠・制約を確認<ExternalLink /></a></div>
        </section>
      </div>
    </>
  )
}
